/**
 * Badge component — Brand Book §2.2
 * 20px height, 0px radius, overline font (9px/700/uppercase/0.8px tracking)
 * Semantic color at 6–8% opacity background, full-strength text
 * Never interactive — no click handlers
 * Max 2 words, always uppercase
 */

import { clsx } from 'clsx';

export interface BadgeProps {
  variant?:
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'neutral'
    | 'secondary'
    | 'purpose'
    | 'upgrade'
    | 'confidence'
    | 'new';
  size?: 'sm' | 'md';
  children: React.ReactNode;
  className?: string;
}

export function Badge({ variant = 'neutral', size = 'md', children, className }: BadgeProps) {
  // Canonical variants per brand book §2.2
  const variants = {
    success: 'bg-success-light text-success',
    warning: 'bg-warning-light text-warning',
    error: 'bg-error-light text-error',
    info: 'bg-info-light text-info',
    neutral: 'bg-surface-subtle text-txt-secondary',
    purpose: 'bg-teal-light text-teal',
    upgrade: 'bg-orange-light text-orange',
    // Non-canonical additions — keep for backward compatibility
    secondary: 'bg-surface-muted text-txt-secondary',
    confidence: 'bg-surface-subtle text-txt-tertiary',
    new: 'bg-orange-light text-orange',
  };

  const sizes = {
    sm: 'h-4 px-1.5',   // 16px — compact for table cells
    md: 'h-5 px-2',     // 20px — standard per spec
  };

  return (
    <span
      className={clsx(
        'inline-flex items-center',
        'font-sans text-overline uppercase',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </span>
  );
}